import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  DeviceLog,
  DeviceStatusEvent,
  FeatureSessionEvent,
  LogType,
  LogLevel,
  MemoryReport,
} from '@/types';
import { useAuthStore } from '@/stores/auth.store';

// Get token from multiple sources for robustness
function getToken(): string | null {
  const storeToken = useAuthStore.getState().getAccessToken();
  if (storeToken) return storeToken;

  const directToken = useAuthStore.getState().accessToken;
  if (directToken) return directToken;

  const localToken = localStorage.getItem('token');
  if (localToken) return localToken;

  return null;
}

interface StreamedMessage {
  device_id: string;
  tenant_id: string;
  log_type: string;
  level: string;
  message: string;
  metadata?: Record<string, unknown>;
  timestamp: string;
}

interface UseDeviceStreamOptions {
  deviceId?: string;         // If provided, subscribe to specific device
  enabled?: boolean;
  maxLogs?: number;
  // When false, incoming log messages are dropped (not pushed to state or
  // console). The WebSocket stays connected for status/presence so the
  // device status indicator keeps working.
  enableLogs?: boolean;
  // When false, memory reports are dropped. The hub also gates memory
  // delivery server-side when set_memory=false, so the device stops
  // publishing onMemoryUsage via MQTT after the 20s grace window.
  enableMemory?: boolean;
  onStatusChange?: (event: DeviceStatusEvent) => void;
  // Fired when a device-global feature session starts or stops for any reason,
  // including one nobody in this tab triggered.
  onFeatureSession?: (event: FeatureSessionEvent) => void;
}

interface UseDeviceStreamReturn {
  /** Snapshot accessor for the live log buffer. The full array lives in
   *  a ref so it never enters React state or the context value — which
   *  would let React 19's dev profiler structured-clone it on every
   *  render and OOM the tab once the buffer grows past a few thousand
   *  rows. Consumers should call this inside render and either feed the
   *  result into local useMemos or pass it down via callback-style
   *  props, never store the returned array in their own state. */
  getLogs: () => DeviceLog[];
  /** Monotonic tick incremented on every buffer mutation. Use as a
   *  useMemo / useEffect dep to react to new logs without putting the
   *  array itself in any reactive primitive. */
  logsVersion: number;
  memoryReports: MemoryReport[];
  status: DeviceStatusEvent | null;
  isConnected: boolean;
  error: Error | null;
  clearLogs: () => void;
  clearMemoryReports: () => void;
  reconnect: () => void;
}

const API_BASE = `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/api/v1`;

// A connection must stay open at least this long before we treat it as healthy
// and reset the reconnect backoff. A socket the server tears down sooner (e.g.
// auth rejected → close 4001) never reaches this point, so repeated failures
// keep backing off exponentially instead of pinning the delay at its 1s floor
// and hammering the server ~1/s.
const STABLE_CONNECTION_MS = 10_000;

export function useDeviceStream({
  deviceId,
  enabled = true,
  maxLogs = 100,
  enableLogs = true,
  enableMemory = true,
  onStatusChange,
  onFeatureSession,
}: UseDeviceStreamOptions = {}): UseDeviceStreamReturn {
  // Mirror enableLogs into a ref so the WS onmessage handler sees the latest
  // value without forcing a reconnect every time the flag flips.
  const enableLogsRef = useRef(enableLogs);
  useEffect(() => {
    enableLogsRef.current = enableLogs;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ action: 'set_logs', enabled: enableLogs }));
    }
  }, [enableLogs]);

  // Same treatment for memory-report gating.
  const enableMemoryRef = useRef(enableMemory);
  useEffect(() => {
    enableMemoryRef.current = enableMemory;
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ action: 'set_memory', enabled: enableMemory }));
    }
  }, [enableMemory]);

  // Live log buffer lives in a ref, NOT useState. See the comment on
  // UseDeviceStreamReturn.getLogs for the OOM rationale. logsVersion is
  // the cheap subscribe-trigger that pushes consumers to re-read via
  // getLogs() — a number is safe to keep in React state and context.
  const logsRef = useRef<DeviceLog[]>([]);
  const [logsVersion, setLogsVersion] = useState(0);
  const updateLogs = useCallback((updater: (prev: DeviceLog[]) => DeviceLog[]) => {
    logsRef.current = updater(logsRef.current);
    setLogsVersion((v) => v + 1);
  }, []);
  const getLogs = useCallback(() => logsRef.current, []);

  const [memoryReports, setMemoryReports] = useState<MemoryReport[]>([]);
  const [status, setStatus] = useState<DeviceStatusEvent | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttempts = useRef(0);
  // Timer that, once a connection has stayed open for STABLE_CONNECTION_MS,
  // resets the reconnect backoff. Cleared on close/unmount so a fast failure
  // never resets the backoff.
  const stableTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Flipped by cleanup so `onclose` (which fires asynchronously AFTER the
  // cleanup function returns) skips the reconnect schedule. Without this,
  // fast nav in/out of a device detail leaves zombie WebSockets behind
  // because close() → onclose → setTimeout → connect() races the
  // React unmount.
  const isUnmountedRef = useRef(false);
  const onStatusChangeRef = useRef(onStatusChange);
  const onFeatureSessionRef = useRef(onFeatureSession);

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  useEffect(() => {
    onFeatureSessionRef.current = onFeatureSession;
  }, [onFeatureSession]);

  // Trim logs when maxLogs changes
  useEffect(() => {
    queueMicrotask(() => updateLogs((prev) => (prev.length > maxLogs ? prev.slice(0, maxLogs) : prev)));
  }, [maxLogs, updateLogs]);

  const clearLogs = useCallback(() => {
    updateLogs(() => []);
  }, [updateLogs]);

  const connectRef = useRef<() => void>(() => {});

  const connect = useCallback(() => {
    if (!enabled) return;

    const accessToken = getToken();
    if (!accessToken) {
      console.log('[DeviceStream] No access token available');
      return;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const apiUrl = new URL(API_BASE);
    const wsBase = `${wsProtocol}//${apiUrl.host}${apiUrl.pathname}`;

    // Use device-specific endpoint if deviceId provided, otherwise all logs
    const wsPath = deviceId
      ? `${wsBase}/ws/devices/${deviceId}/logs`
      : `${wsBase}/ws/logs`;

    // Connect without token in URL; authenticate via first message
    const wsUrl = wsPath;
    console.log('[DeviceStream] Connecting:', deviceId || 'all devices');

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[DeviceStream] Connected');
        // Send auth token as first message (RFC-safe: no JWT in URL)
        ws.send(JSON.stringify({ type: 'auth', token: accessToken }));
        // Tell the hub whether we currently want log-type / memory messages.
        // Both default to false server-side, so without these set_* messages
        // the backend silently drops the corresponding broadcasts — which is
        // what we want when the UI isn't actively monitoring either.
        ws.send(JSON.stringify({ action: 'set_logs', enabled: enableLogsRef.current }));
        ws.send(JSON.stringify({ action: 'set_memory', enabled: enableMemoryRef.current }));
        setIsConnected(true);
        setError(null);
        // Reset the backoff only once the connection proves STABLE — not here
        // on open. The server authenticates lazily and closes (code 4001)
        // within a few hundred ms if the token is rejected; resetting on open
        // would pin the backoff at 1s and turn a persistent auth failure into a
        // 1/s reconnect storm. See STABLE_CONNECTION_MS.
        if (stableTimeoutRef.current) clearTimeout(stableTimeoutRef.current);
        stableTimeoutRef.current = setTimeout(() => {
          reconnectAttempts.current = 0;
        }, STABLE_CONNECTION_MS);
      };

      ws.onmessage = (event) => {
        try {
          const messages = event.data.split('\n').filter(Boolean);

          for (const msgStr of messages) {
            const msg: StreamedMessage = JSON.parse(msgStr);

            // Typed envelopes carry `type` instead of `log_type`. Handle and
            // skip them — none of the log_type branches below would match, but
            // returning early keeps the intent obvious.
            if ((msg as unknown as { type?: string }).type === 'feature_session') {
              onFeatureSessionRef.current?.(msg as unknown as FeatureSessionEvent);
              continue;
            }

            // Handle status/presence messages
            if (msg.log_type === 'status' || msg.log_type === 'presence') {
              const statusValue = (msg.metadata?.status as string) ||
                                  (msg.log_type === 'presence' && msg.message?.includes('offline') ? 'offline' : 'online');
              const statusEvent: DeviceStatusEvent = {
                device_id: msg.device_id,
                tenant_id: msg.tenant_id,
                status: statusValue as 'online' | 'offline' | 'pending' | 'blocked',
                ip_address: msg.metadata?.ip_address as string | undefined,
                cpu_percent: msg.metadata?.cpu_percent as number | undefined,
                memory_kb: msg.metadata?.memory_kb as number | undefined,
                timestamp: msg.timestamp,
              };
              setStatus(statusEvent);
              onStatusChangeRef.current?.(statusEvent);
            }

            // Handle log messages (system, app, activity, kernel, etc.)
            // Skip entirely when live mode is off so state/console stay clean
            // even if the backend hasn't yet stopped device broadcast.
            if (enableLogsRef.current && (
                msg.log_type === 'system' || msg.log_type === 'app' ||
                msg.log_type === 'crash' || msg.log_type === 'network' ||
                msg.log_type === 'security' || msg.log_type === 'command' ||
                msg.log_type === 'activity' || msg.log_type === 'kernel')) {
              const log: DeviceLog = {
                id: `${msg.device_id}-${msg.timestamp}-${Math.random()}`,
                device_id: msg.device_id,
                log_type: msg.log_type as LogType,
                level: (msg.level || 'info') as LogLevel,
                message: msg.message,
                metadata: msg.metadata || {},
                created_at: msg.timestamp,
              };

              updateLogs((prev) => {
                const newLogs = [log, ...prev];
                return newLogs.length > maxLogs ? newLogs.slice(0, maxLogs) : newLogs;
              });
            }

            // Handle memory reports (for chart). Gated by enableMemory so a
            // late-arriving broadcast during the backend disable grace window
            // doesn't skew the chart after the user stopped the monitor.
            if (enableMemoryRef.current && msg.log_type === 'memory') {
              const report: MemoryReport = {
                totalKb: (msg.metadata?.totalKb as number) || 0,
                usedKb: (msg.metadata?.usedKb as number) || 0,
                freeKb: (msg.metadata?.freeKb as number) || 0,
                availableKb: (msg.metadata?.availableKb as number) || 0,
                timestamp: msg.timestamp,
              };
              setMemoryReports((prev) => {
                const newReports = [...prev, report];
                // Keep last 60 data points (5 minutes at 5s interval).
                // The live MemoryMonitor chart's sliding X domain is
                // pinned to the same 5-minute window, so older samples
                // are invisible anyway — keeping them in state would
                // just bloat the buffer. For longer ranges (1h / 24h /
                // 7d) we'll wire the ClickHouse analytics endpoint
                // (device_metrics) into the chart later instead of
                // raising this cap; see memory-monitor.tsx LIVE_WINDOW_MS
                // for the matching note.
                return newReports.length > 60 ? newReports.slice(-60) : newReports;
              });
            }
          }
        } catch (e) {
          console.error('[DeviceStream] Parse error:', e);
        }
      };

      ws.onerror = () => {
        setError(new Error('WebSocket connection error'));
      };

      ws.onclose = (event) => {
        console.log('[DeviceStream] Closed:', event.code, event.wasClean);
        setIsConnected(false);
        wsRef.current = null;
        // A close cancels any pending "connection is stable" reset, so a fast
        // failure (e.g. auth-reject close 4001) keeps the backoff growing.
        if (stableTimeoutRef.current) {
          clearTimeout(stableTimeoutRef.current);
          stableTimeoutRef.current = null;
        }

        if (isUnmountedRef.current) return;
        const intentionalClose = event.code === 1000;
        if (enabled && !intentionalClose) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current++;
          reconnectTimeoutRef.current = setTimeout(() => connectRef.current(), delay);
        }
      };
    } catch (e) {
      setError(e as Error);
    }
  }, [deviceId, enabled, maxLogs]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const reconnect = useCallback(() => {
    reconnectAttempts.current = 0;
    connect();
  }, [connect]);

  const accessToken = useAuthStore((state) => state.accessToken);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    if ((accessToken || isAuthenticated) && enabled) {
      queueMicrotask(() => connect());
    } else {
      // Disconnect when not enabled
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
        queueMicrotask(() => setIsConnected(false));
      }
    }

    return () => {
      isUnmountedRef.current = true;
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (stableTimeoutRef.current) clearTimeout(stableTimeoutRef.current);
      if (wsRef.current) wsRef.current.close(1000, 'unmount');
    };
  }, [connect, accessToken, isAuthenticated, enabled]);

  return {
    getLogs,
    logsVersion,
    memoryReports,
    status,
    isConnected,
    error,
    clearLogs,
    clearMemoryReports: useCallback(() => setMemoryReports([]), []),
    reconnect,
  };
}
